import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  ChevronDownIcon, 
  XIcon 
} from 'lucide-react';
import { Tenant, Customer, tenantServices, customerServices, supabase } from '../lib/supabase';

const Tenants = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [currentTenant, setCurrentTenant] = useState<Partial<Tenant> | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Fetch tenants and customers data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Get tenants
        const { data: tenantsData, error: tenantsError } = await tenantServices.getAll();
        if (tenantsError) throw new Error('Failed to fetch tenants');
        setTenants(tenantsData || []);
        
        // Get customers for the dropdown
        const { data: customersData, error: customersError } = await customerServices.getAll();
        if (customersError) throw new Error('Failed to fetch customers');
        setCustomers(customersData || []);
        
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Open modal to add/edit tenant
  const handleOpenModal = (tenant: Tenant | null = null) => {
    setCurrentTenant(tenant || { 
      company_id: '', 
      name: '', 
      status: 'Active', 
      health_score: 75 
    });
    setShowModal(true);
  };
  
  // Close modal and reset form
  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentTenant(null);
  };
  
  // Handle tenant form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentTenant || !currentTenant.name || !currentTenant.company_id) {
      setError('Please fill all required fields');
      return;
    }
    
    try {
      let result: { data: Tenant[] | null; error: Error | null };
      
      if (currentTenant.id) {
        // Update existing tenant
        result = await tenantServices.update(currentTenant.id, currentTenant);
        
        // Update local state
        setTenants(prevTenants => 
          prevTenants.map(tenant => 
            tenant.id === currentTenant.id 
              ? { ...tenant, ...currentTenant } 
              : tenant
          )
        );
      } else {
        // Create new tenant
        const newTenant = {
          ...currentTenant,
          user_id: (await supabase.auth.getUser()).data.user?.id, // Get the current authenticated user ID
        } as Tenant;
        
        result = await tenantServices.create(newTenant);
        
        // Update local state
        if (result.data && result.data.length > 0) {
          const newTenantData = result.data[0];
          setTenants(prevTenants => [newTenantData, ...prevTenants]);
        }
      }
      
      handleCloseModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    }
  };
  
  // Handle tenant deletion
  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this tenant?')) return;
    
    try {
      await tenantServices.delete(id);
      setTenants(prevTenants => prevTenants.filter(tenant => tenant.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    }
  };
  
  // Filter tenants based on search query
  const filteredTenants = tenants.filter(tenant => 
    tenant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    getCompanyName(tenant.company_id)?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tenant.status.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Get company name from ID
  function getCompanyName(companyId: string) {
    return customers.find(c => c.id === companyId)?.name || 'Unknown Company';
  }
  
  // Get status color class
  function getStatusColor(status: string) {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'at risk':
        return 'bg-yellow-100 text-yellow-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  }
  
  // Get health score color class
  function getHealthScoreColor(score: number) {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Tenants</h1>
        <button
          onClick={() => handleOpenModal()}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          <PlusIcon size={16} className="mr-1" /> Add Tenant
        </button>
      </div>
      
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded flex justify-between items-center">
          {error}
          <XIcon size={16} className="cursor-pointer" onClick={() => setError('')} />
        </div>
      )}
      
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search tenants..."
          className="w-full md:w-64 px-4 py-2 border rounded-md"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
      
      {isLoading ? (
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          {filteredTenants.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              {searchQuery ? 'No tenants matching your search.' : 'No tenants found. Add your first tenant!'}
            </div>
          ) : (
            <table className="min-w-full bg-white rounded-lg overflow-hidden">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-3 px-4 text-left text-sm font-medium text-gray-600">Tenant Name</th>
                  <th className="py-3 px-4 text-left text-sm font-medium text-gray-600">Company</th>
                  <th className="py-3 px-4 text-left text-sm font-medium text-gray-600">Status</th>
                  <th className="py-3 px-4 text-left text-sm font-medium text-gray-600">Health Score</th>
                  <th className="py-3 px-4 text-right text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredTenants.map((tenant) => (
                  <tr key={tenant.id} className="hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">
                      <Link to={`/tenants/${tenant.id}`} className="text-blue-600 hover:text-blue-800">
                        {tenant.name}
                      </Link>
                    </td>
                    <td className="py-3 px-4">{getCompanyName(tenant.company_id)}</td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(tenant.status)}`}>
                        {tenant.status}
                      </span>
                    </td>
                    <td className={`py-3 px-4 font-medium ${getHealthScoreColor(tenant.health_score)}`}>
                      {tenant.health_score}%
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button 
                        onClick={() => handleOpenModal(tenant)}
                        className="text-gray-600 hover:text-blue-600 mr-2"
                      >
                        <PencilIcon size={16} />
                      </button>
                      <button 
                        onClick={() => tenant.id && handleDelete(tenant.id)}
                        className="text-gray-600 hover:text-red-600"
                      >
                        <TrashIcon size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Add/Edit Tenant Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6 relative">
            <button 
              onClick={handleCloseModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <XIcon size={20} />
            </button>
            
            <h2 className="text-xl font-semibold mb-4">
              {currentTenant?.id ? 'Edit Tenant' : 'Add New Tenant'}
            </h2>
            
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company*
                </label>
                <div className="relative">
                  <select
                    value={currentTenant?.company_id || ''}
                    onChange={(e) => setCurrentTenant({
                      ...currentTenant,
                      company_id: e.target.value
                    })}
                    className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                    required
                  >
                    <option value="">Select a company</option>
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <ChevronDownIcon size={16} />
                  </div>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tenant Name*
                </label>
                <input
                  type="text"
                  value={currentTenant?.name || ''}
                  onChange={(e) => setCurrentTenant({
                    ...currentTenant,
                    name: e.target.value
                  })}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={currentTenant?.status || 'Active'}
                  onChange={(e) => setCurrentTenant({
                    ...currentTenant,
                    status: e.target.value
                  })}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="Active">Active</option>
                  <option value="At Risk">At Risk</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Health Score (0-100)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={currentTenant?.health_score || 75}
                  onChange={(e) => setCurrentTenant({
                    ...currentTenant,
                    health_score: parseInt(e.target.value, 10)
                  })}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {currentTenant?.id ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tenants;
